import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';

/**
 * Command to clear color from selected transcript elements
 */
export class TranscriptClearColorCommand extends AbstractTranscriptEditorCommand {
  private elementIds: string[];
  private previousColors: Map<string, string | undefined> = new Map();

  constructor(
    editor: TranscriptEditorCore,
    elementIds: string[],
    userId: string
  ) {
    super(`Clear color from ${elementIds.length} element(s)`, editor);
    this.elementIds = elementIds;
    
    // Store metadata for debugging
    this.setMetadata('elementIds', elementIds);
    this.setMetadata('userId', userId);
  }

  async execute(): Promise<boolean> {
    try {
      const container = this.editor.getContainer();
      if (!container) return false;

      // Process each element
      for (const elementId of this.elementIds) {
        // Find the element across all segments and paragraphs
        for (const segment of container.segments) {
          for (const paragraph of segment.paragraphs) {
            const element = paragraph.elements.find(e => e.id === elementId);
            if (element && element.color) {
              // Store the previous color for undo
              this.previousColors.set(elementId, element.color);
              
              // Clear the color
              element.color = undefined;
              
              // Increment counters to trigger reactivity
              element.counter++;
              paragraph.counter++;
              
              // Record the operation
              this.editor.recordOperation({
                type: 'edit',
                data: {
                  elementId,
                  previousColor: this.previousColors.get(elementId),
                  action: 'color_cleared'
                },
                userId: this.getMetadata('userId'),
                timestamp: Date.now()
              });
            }
          }
        }
      }

      // Notify observers about the color change
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_MODIFIED, {
        elementIds: this.elementIds,
        changeType: 'color_cleared',
        container: container
      });

      return true;
    } catch (error) {
      console.error('Error executing TranscriptClearColorCommand:', error);
      return false;
    }
  }

  async undo(): Promise<boolean> {
    try {
      const container = this.editor.getContainer();
      if (!container) return false;

      // Restore previous colors
      for (const [elementId, previousColor] of this.previousColors) {
        for (const segment of container.segments) {
          for (const paragraph of segment.paragraphs) {
            const element = paragraph.elements.find(e => e.id === elementId);
            if (element) {
              element.color = previousColor;
              
              // Increment counters to trigger reactivity
              element.counter++;
              paragraph.counter++;
            }
          }
        }
      }

      // Notify observers about the color restoration
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_MODIFIED, {
        elementIds: this.elementIds,
        changeType: 'color_restored',
        container: container
      });

      return true;
    } catch (error) {
      console.error('Error undoing TranscriptClearColorCommand:', error);
      return false;
    }
  }
}